// The committed-Looks library (#105) — for the camera selected in the editor's ship bar, it lists
// that camera's committed Looks and manages them: Edit (re-open in the editor), Rename, Delete. The
// editor (LooksPage) owns the camera selection + Commit + the debounced Test-look save; this panel
// is the list half. A committed Look carries its Palette inline, so it is self-contained.

import { useEffect, useState } from "react";
import { ApiError, type ApiClient, type LookSummary } from "../api";
import type { EditableLook } from "./LookPortal";
import { wireLookToEditable } from "./lookWire";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { RefreshErrorBanner } from "./RefreshErrorBanner";

interface Status { kind: "ok" | "error"; text: string; }

/** The camera-side Look cap — mirrors the Worker's "look library full (max 44)" refusal, so
 *  the owner sees the budget BEFORE an unarchive bounces off it. */
const CAMERA_LOOK_CAP = 44;

export interface LookLibraryProps {
  client: ApiClient;
  /** The selected camera (from the editor's ship bar). Empty until a camera is selected. */
  deviceId: string;
  /** Whether the owner has any claimed camera (drives the empty state). */
  hasDevices: boolean;
  /** Bumped by LooksPage after a commit so the list re-fetches. */
  reloadKey: number;
  /** Re-open a committed Look in the editor (load it to tweak + re-commit). */
  onLoadLook?: (look: EditableLook) => void;
}

export function LookLibrary({ client, deviceId, hasDevices, reloadKey, onLoadLook }: LookLibraryProps): React.JSX.Element {
  const [looks, setLooks] = useState<LookSummary[]>([]);
  const [localReload, setLocalReload] = useState(0); // bumped by rename/delete
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Name filter — a library can hold 44 on-camera Looks plus an unbounded archived shelf
  // (ADR-0057); scanning one flat list by eye doesn't scale.
  const [query, setQuery] = useState("");

  const fail = (e: unknown, fallback: string): void =>
    setStatus({ kind: "error", text: e instanceof Error ? e.message : fallback });

  // The selected camera's library (re-fetched on device change, after a commit, or a mutation).
  useEffect(() => {
    if (!deviceId) { setLooks([]); setLoadError(null); return; }
    let live = true;
    setLoading(true);
    setLoadError(null);
    client.listLooks(deviceId)
      .then((ls) => { if (live) { setLooks(ls); setLoading(false); } })
      .catch((e) => {
        // Distinguish a fetch FAILURE from an empty library — the old code collapsed both
        // to "no Looks" with no retry (#139). Keep the last-good list; surface the error.
        if (live) {
          setLoadError(e instanceof Error ? e.message : "Couldn’t load Looks.");
          setLoading(false);
        }
      });
    return () => { live = false; };
  }, [client, deviceId, reloadKey, localReload]);

  const reload = (): void => setLocalReload((k) => k + 1);

  async function onLoad(l: LookSummary): Promise<void> {
    if (!onLoadLook) return;
    try {
      // The editor re-seeds visibly (and LooksPage may first confirm if there are unsaved edits),
      // so the editor change is the feedback — no separate status line here. Thread the library
      // row's immutable id into editor state so a re-commit updates this Look in place (#161).
      onLoadLook(wireLookToEditable(await client.getLook(deviceId, l.id), l.id));
    } catch (e) {
      fail(e, "Load failed.");
    }
  }
  async function onRename(l: LookSummary, name: string): Promise<void> {
    try {
      await client.renameLook(deviceId, l.id, name);
      setStatus({ kind: "ok", text: `Renamed to “${name}”.` });
      reload();
    } catch (e) {
      fail(e, "Rename failed.");
    }
  }
  async function onDelete(l: LookSummary): Promise<void> {
    try {
      await client.deleteLook(deviceId, l.id);
      setStatus({ kind: "ok", text: `Deleted “${l.name}”. The camera drops it on its next sync.` });
      reload();
    } catch (e) {
      fail(e, "Delete failed.");
    }
  }
  async function onArchive(l: LookSummary): Promise<void> {
    try {
      await client.archiveLook(deviceId, l.id);
      // Mirror the delete copy — the camera consequence is the same (it drops the Look next sync);
      // the difference is the Look stays here, editable, on the shelf (ADR-0057).
      setStatus({ kind: "ok", text: `Archived “${l.name}”. The camera drops it on its next sync.` });
      reload();
    } catch (e) {
      fail(e, "Archive failed.");
    }
  }
  async function onUnarchive(l: LookSummary): Promise<void> {
    try {
      await client.unarchiveLook(deviceId, l.id);
      setStatus({ kind: "ok", text: `Unarchived “${l.name}”. It returns to the camera on its next sync.` });
      reload();
    } catch (e) {
      // The camera holds at most 44 Looks; unarchiving into a full library is refused (409). Surface the
      // actionable message rather than the raw "look library full (max 44)" from the Worker.
      if (e instanceof ApiError && e.status === 409) {
        setStatus({ kind: "error", text: "Camera library full — archive or delete a Look first." });
        return;
      }
      fail(e, "Unarchive failed.");
    }
  }

  const activeCount = looks.filter((l) => !l.archived).length;
  const archivedCount = looks.length - activeCount;
  const q = query.trim().toLowerCase();
  const shown = q ? looks.filter((l) => l.name.toLowerCase().includes(q)) : looks;

  return (
    <section className="looklibrary" aria-label="Look library">
      {/* The split + cap make the camera budget visible BEFORE an unarchive bounces off the
          409 — "34 / 44 on camera · 12 archived", not one undifferentiated total. */}
      <h3 className="looklibrary__title" data-testid="lib-title">
        Library
        {looks.length
          ? ` — ${activeCount} / ${CAMERA_LOOK_CAP} on camera${archivedCount ? ` · ${archivedCount} archived` : ""}`
          : ""}
      </h3>
      {!hasDevices ? (
        <p className="looklibrary__empty">No claimed cameras yet — claim one to commit Looks.</p>
      ) : loadError && looks.length === 0 ? (
        // A fetch FAILURE (not an empty library) — surfaced with a Retry, no longer
        // silently shown as "no Looks committed yet" (#139). headingLevel=4 keeps the
        // outline ordered under this panel's h3 title (a default h2 would invert it).
        <ErrorState title="Couldn’t load Looks" message={loadError} onRetry={reload} headingLevel={4} />
      ) : loading && looks.length === 0 ? (
        <Loading label="Loading Looks…" />
      ) : (
        <>
          {loadError && (
            <RefreshErrorBanner
              className="looklibrary__msg looklibrary__msg--error"
              message={loadError}
              onRetry={reload}
              testId="lib-load-error"
            />
          )}
          {status && (
            <p
              className={`looklibrary__msg looklibrary__msg--${status.kind}`}
              role={status.kind === "error" ? "alert" : "status"}
              data-testid="lib-msg"
            >
              {status.text}
            </p>
          )}
          {looks.length > 5 && (
            <input
              className="looklibrary__search"
              type="search"
              value={query}
              placeholder="Filter by name"
              aria-label="Filter Looks by name"
              data-testid="lib-search"
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <ul className="looklibrary__list">
            {shown.map((l) => (
              <LookRow
                key={l.id}
                look={l}
                onLoad={onLoadLook ? () => onLoad(l) : undefined}
                onRename={(name) => onRename(l, name)}
                onDelete={() => onDelete(l)}
                onArchive={() => onArchive(l)}
                onUnarchive={() => onUnarchive(l)}
              />
            ))}
            {looks.length === 0 && <li className="looklibrary__empty">No Looks committed yet.</li>}
            {looks.length > 0 && shown.length === 0 && (
              <li className="looklibrary__empty" data-testid="lib-filter-empty">
                No Looks match “{query.trim()}”.
              </li>
            )}
          </ul>
        </>
      )}
    </section>
  );
}

interface LookRowProps {
  look: LookSummary;
  onLoad?: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  /** Shelve the Look (one-tap, reversible): kept in the portal, dropped from the camera (ADR-0057). */
  onArchive: () => Promise<void>;
  /** Return an archived Look to the camera (one-tap); refused with a message when the camera is full. */
  onUnarchive: () => Promise<void>;
}

/** One library row: Edit / Rename / Archive|Unarchive / Delete, with inline rename (31-char cap) + a
 *  two-step delete confirm (mirroring DeviceManager's idiom) — no blocking window.prompt/confirm. Archive
 *  and Unarchive are one-tap (reversible, so no confirm); an Archived Look renders dimmed + badged and
 *  shows Unarchive in place of Archive. */
function LookRow({ look, onLoad, onRename, onDelete, onArchive, onUnarchive }: LookRowProps): React.JSX.Element {
  const [mode, setMode] = useState<"idle" | "rename" | "delete">("idle");
  const [name, setName] = useState(look.name);
  const [busy, setBusy] = useState(false);

  async function saveRename(): Promise<void> {
    const nm = name.trim();
    if (!nm || nm === look.name) { setMode("idle"); return; }
    setBusy(true);
    await onRename(nm);
    setBusy(false);
    setMode("idle");
  }
  async function confirmDelete(): Promise<void> {
    setBusy(true);
    await onDelete();
    setBusy(false);
    setMode("idle"); // the row is usually gone on reload; harmless if not
  }
  async function doArchive(): Promise<void> {
    setBusy(true);
    await onArchive();
    setBusy(false); // the row re-renders dimmed on reload
  }
  async function doUnarchive(): Promise<void> {
    setBusy(true);
    await onUnarchive();
    setBusy(false); // on success the row re-renders live; on a full-library 409 it stays archived
  }

  if (mode === "rename") {
    return (
      <li className="looklibrary__item">
        <input
          className="looklibrary__rename"
          value={name}
          maxLength={31}
          autoFocus
          aria-label={`New name for ${look.name}`}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void saveRename();
            else if (e.key === "Escape") { setName(look.name); setMode("idle"); }
          }}
        />
        <button type="button" onClick={() => void saveRename()} disabled={busy || !name.trim()}>Save</button>
        <button type="button" onClick={() => { setName(look.name); setMode("idle"); }} disabled={busy}>Cancel</button>
      </li>
    );
  }
  if (mode === "delete") {
    return (
      <li className="looklibrary__item">
        <span className="looklibrary__name">{look.name}</span>
        <span className="looklibrary__confirm-q">Delete?</span>
        <button
          type="button"
          className="looklibrary__del"
          onClick={() => void confirmDelete()}
          disabled={busy}
          aria-label={`Confirm delete ${look.name}`}
        >
          {busy ? "Deleting…" : "Yes, delete"}
        </button>
        <button type="button" onClick={() => setMode("idle")} disabled={busy}>Cancel</button>
      </li>
    );
  }
  return (
    <li
      className={`looklibrary__item${look.archived ? " looklibrary__item--archived" : ""}`}
      data-testid="lib-item"
    >
      <span className="looklibrary__name">{look.name}</span>
      {look.archived && (
        // The ONLY signal that this Look isn't on the camera — dimmed row + this badge (ADR-0057).
        <span className="looklibrary__badge" data-testid="lib-archived-badge">Archived</span>
      )}
      {onLoad && (
        <button type="button" onClick={onLoad} aria-label={`edit ${look.name}`}>Edit</button>
      )}
      <button type="button" onClick={() => { setName(look.name); setMode("rename"); }} aria-label={`rename ${look.name}`}>Rename</button>
      {look.archived ? (
        // One-tap return to the camera; the parent surfaces a "camera full" message on a 409.
        <button type="button" onClick={() => void doUnarchive()} disabled={busy} aria-label={`unarchive ${look.name}`}>Unarchive</button>
      ) : (
        // One-tap, reversible — no confirm (unlike the irreversible Delete).
        <button type="button" onClick={() => void doArchive()} disabled={busy} aria-label={`archive ${look.name}`}>Archive</button>
      )}
      <button type="button" onClick={() => setMode("delete")} aria-label={`delete ${look.name}`}>Delete</button>
    </li>
  );
}
